// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.0
// updated  : 2026-06-20
// protected: NO
// changelog: v1.0 (Fix Batch UAT-02 / B) - QR Tracking READ-ONLY, terbuka semua role
//            terdaftar, lintas region/store. Scan kamera / input manual / scan-gun.
// =============================================================================
import React, { useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScanLine, Loader2, QrCode, History, AlertCircle, ServerCrash } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { QrCameraButton } from "@/v2/components/QrCameraButton";
import { apiErrorInfo } from "@/v2/components/ux";

const MOVE_LABEL = {
  PRODUCED: "Produksi Selesai", TO_WAREHOUSE: "Masuk Gudang", RECEIVED_WAREHOUSE: "Diterima Gudang",
  TRANSFER_OUT: "Transfer Keluar", TRANSFER_IN: "Transfer Masuk", SHIP_TO_STORE: "Dikirim ke Store",
  RECEIVED_STORE: "Diterima Store", SOLD: "Terjual", COMPLIMENT: "Compliment", RETURN: "Retur",
  CANCEL_REVERSAL: "Pembatalan (Reversal)", OPENING_BALANCE_ADJUSTMENT: "Koreksi Saldo Awal",
};
const fmt = (s) => { if (!s) return "-"; try { return new Date(s).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }); } catch { return s; } };
const Field = ({ label, value }) => (
  <div><div className="text-[11px] text-muted-foreground">{label}</div><div className="text-sm font-medium break-words">{value ?? "-"}</div></div>
);

export default function QrTrackingPage() {
  const { api } = useV2Auth();
  const [qr, setQr] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [errKind, setErrKind] = useState("not_found");

  const track = useCallback(async (raw) => {
    const q = (raw ?? qr).trim();
    if (!q) return;
    setBusy(true); setErr(""); setData(null);
    try {
      const r = await api(`/api/v2/qr-tracking/${encodeURIComponent(q)}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const info = apiErrorInfo(r, d, "QR ini tidak ditemukan di sistem. Periksa kembali kode QR-nya.");
        setErrKind(info.kind); setErr(info.message);
        return;
      }
      setData(d);
    } catch { setErrKind("system_error"); setErr("Gagal terhubung ke server. Periksa koneksi lalu coba lagi."); toast.error("Gagal memuat data tracking"); }
    finally { setBusy(false); }
  }, [api, qr]);

  const onCam = (code) => { setQr(code); track(code); };

  return (
    <div className="space-y-5" data-testid="v2-qr-tracking-page">
      <div>
        <h2 className="text-2xl font-semibold flex items-center gap-2"><QrCode className="w-6 h-6" /> QR Tracking</h2>
        <p className="text-sm text-muted-foreground">Lacak riwayat lengkap merchandize lintas region. Read-only — tidak mengubah data.</p>
      </div>

      <Card className="p-5 rounded-2xl">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <ScanLine className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={qr} placeholder="Scan / ketik QR item lalu Enter" className="pl-9" data-testid="v2-qrt-input"
              onChange={(e) => setQr(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); track(); } }} />
          </div>
          <Button onClick={() => track()} disabled={busy} data-testid="v2-qrt-search">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Lacak"}
          </Button>
          <QrCameraButton onScan={onCam} testid="v2-qrt-cam" />
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Kamera HP hanya untuk membaca QR. Mendukung input manual &amp; scan-gun.</p>
      </Card>

      {err && (
        errKind === "system_error" ? (
          <Card className="p-5 rounded-2xl flex items-start gap-3 border-[hsl(var(--badge-warning))]/40 bg-[hsl(var(--badge-warning))]/5" data-testid="v2-qrt-error">
            <ServerCrash className="w-5 h-5 shrink-0 mt-0.5 text-[hsl(var(--badge-warning))]" />
            <div>
              <div className="text-sm font-semibold text-[hsl(var(--badge-warning))]">Gangguan sistem, bukan data hilang</div>
              <div className="text-sm text-muted-foreground mt-0.5">{err}</div>
            </div>
          </Card>
        ) : (
          <Card className="p-5 rounded-2xl flex items-center gap-2 text-destructive" data-testid="v2-qrt-error">
            <AlertCircle className="w-5 h-5" /> {err}
          </Card>
        )
      )}

      {data && (
        <>
          <Card className="p-5 rounded-2xl space-y-4" data-testid="v2-qrt-result">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="font-mono text-sm">{data.qr_id}</div>
              <div className="flex gap-2">
                <Badge variant={data.item_category === "MoU" ? "secondary" : "default"}>{data.item_category || "-"}</Badge>
                <Badge variant="outline">{data.current_state || data.state || "-"}</Badge>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Merchandize" value={data.product_name || data.club} />
              <Field label="SKU" value={data.sku || "-"} />
              <Field label="Klub" value={data.club || "-"} />
              <Field label="Size" value={data.size || "-"} />
              <Field label="Jenis Stok" value={data.item_category} />
              <Field label="Asal Barang" value={data.origin_label || (data.wo_no ? data.wo_no : "-")} />
              <Field label="Nomor WO" value={data.origin === "OPENING_BALANCE" ? "— (Saldo Awal)" : (data.wo_no || "-")} />
              {data.wo_no_offline ? <Field label="WO Offline" value={data.wo_no_offline} /> : null}
              <Field label="Region Pemilik" value={data.owner_region} />
              <Field label="Region Produksi" value={data.production_region} />
              <Field label="Lokasi Saat Ini" value={data.current_location} />
              <Field label="Status" value={data.current_state || data.state} />
              <Field label="Perlu Transfer" value={data.needs_transfer ? "Ya" : "Tidak"} />
            </div>
          </Card>

          <Card className="p-5 rounded-2xl" data-testid="v2-qrt-history">
            <h3 className="font-semibold flex items-center gap-2 mb-4"><History className="w-4 h-4" /> Riwayat Lengkap</h3>
            {(!data.history || data.history.length === 0) ? (
              <p className="text-sm text-muted-foreground">Belum ada riwayat tercatat.</p>
            ) : (
              <ol className="relative border-l-2 border-muted ml-2 space-y-5">
                {data.history.map((h, i) => (
                  <li key={i} className="ml-5" data-testid="v2-qrt-event">
                    <span className="absolute -left-[7px] mt-1.5 w-3 h-3 rounded-full bg-primary" />
                    <div className="flex items-center justify-between flex-wrap gap-1">
                      <span className="font-medium text-sm">{MOVE_LABEL[h.movement_type] || h.movement_type}</span>
                      <span className="text-[11px] text-muted-foreground">{fmt(h.occurred_at)}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {h.from_location ? `${h.from_location} → ` : ""}{h.to_location || h.resulting_state || ""}
                      {h.actor_role ? ` · oleh ${h.actor_role}` : ""}{h.ref_no ? ` · ${h.ref_no}` : ""}
                    </div>
                    {h.note && <div className="text-xs italic text-muted-foreground mt-0.5">{h.note}</div>}
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
